import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  exchangeOperatorToken,
  refreshOperatorToken,
  sendOperatorPincode,
} from "src/lib/operatorAuthService";
import { configureIv3Auth } from "src/lib/iv3Client";
import type { BroadcastEnv } from "src/types/broadcasts";

// Shared across concurrent refresh() callers so simultaneous 401s trigger a
// single refresh request. Module-level (not store state) so it never persists.
let inflightRefresh: Promise<boolean> | null = null;
// Bumped by clearToken so an in-flight refresh that resolves after sign-out is
// discarded instead of writing a token back into localStorage.
let refreshEpoch = 0;

export type OperatorAuthStore = {
  token: string;
  tokenEnv: BroadcastEnv | null;
  /** Email the current token was issued for; used to reject cross-account reuse. */
  tokenEmail: string | null;
  auditId: string | null;
  pincodeRecipient: string | null;
  sendingPincode: boolean;
  exchanging: boolean;
  /** True while a background token refresh is in flight. */
  refreshing: boolean;
  error: string | null;

  clearToken: () => void;
  clearError: () => void;
  resetPincodeFlow: () => void;
  sendPincode: (email: string, env: BroadcastEnv) => Promise<void>;
  exchangeOtp: (
    email: string,
    env: BroadcastEnv,
    verificationCode: string,
  ) => Promise<void>;
  /**
   * Exchange the current token for a fresh one (no OTP). Returns `true` on
   * success. On failure the token is cleared so the app falls back to re-OTP.
   */
  refresh: () => Promise<boolean>;
};

export const useOperatorAuthStore = create<OperatorAuthStore>()(
  persist(
    (set, get) => ({
      token: "",
      tokenEnv: null,
      tokenEmail: null,
      auditId: null,
      pincodeRecipient: null,
      sendingPincode: false,
      exchanging: false,
      refreshing: false,
      error: null,

      clearToken: () => {
        // Invalidate any in-flight refresh so its result can't resurrect the
        // token in localStorage after sign-out.
        refreshEpoch += 1;
        inflightRefresh = null;
        set({
          token: "",
          tokenEnv: null,
          tokenEmail: null,
          refreshing: false,
          error: null,
        });
      },

      clearError: () => set({ error: null }),

      resetPincodeFlow: () =>
        set({
          auditId: null,
          pincodeRecipient: null,
          error: null,
        }),

      sendPincode: async (email, env) => {
        set({ sendingPincode: true, error: null });
        try {
          const res = await sendOperatorPincode(email, env);
          set({
            auditId: res.auditId,
            pincodeRecipient: res.recipient,
            sendingPincode: false,
          });
        } catch (e) {
          set({
            sendingPincode: false,
            error:
              e instanceof Error
                ? e.message
                : "Failed to send verification code.",
          });
        }
      },

      exchangeOtp: async (email, env, verificationCode) => {
        const { auditId } = get();
        if (!auditId) {
          set({ error: "Send a verification code first." });
          return;
        }
        const code = verificationCode.trim();
        if (!code) {
          set({ error: "Enter the verification code from your email." });
          return;
        }
        set({ exchanging: true, error: null });
        try {
          const token = await exchangeOperatorToken(email, env, auditId, code);
          set({
            token,
            tokenEnv: env,
            tokenEmail: email.trim().toLowerCase(),
            exchanging: false,
            auditId: null,
            pincodeRecipient: null,
          });
        } catch (e) {
          set({
            exchanging: false,
            error: e instanceof Error ? e.message : "Verification failed.",
          });
        }
      },

      refresh: async () => {
        // Concurrent 401s share one refresh so the later callers await the
        // in-flight request instead of failing or double-refreshing.
        if (inflightRefresh) return inflightRefresh;
        const { token, tokenEnv } = get();
        if (!token.trim() || !tokenEnv) return false;
        const epoch = refreshEpoch;
        set({ refreshing: true });
        inflightRefresh = (async () => {
          try {
            const next = await refreshOperatorToken(token, tokenEnv);
            // Sign-out happened mid-flight — discard the result rather than
            // writing a token back after the user logged out.
            if (refreshEpoch !== epoch) return false;
            set({ token: next, tokenEnv, refreshing: false, error: null });
            return true;
          } catch {
            if (refreshEpoch !== epoch) return false;
            // Refresh failed (expired/revoked) — drop the token so the gate
            // falls back to a fresh OTP rather than retrying a dead token.
            set({
              token: "",
              tokenEnv: null,
              tokenEmail: null,
              refreshing: false,
            });
            return false;
          } finally {
            // Only clear the shared slot if it's still ours (clearToken may
            // have reset it on sign-out).
            if (refreshEpoch === epoch) inflightRefresh = null;
          }
        })();
        return inflightRefresh;
      },
    }),
    {
      name: "operator-auth",
      storage: createJSONStorage(() => localStorage),
      // Only the durable credential is persisted. Transient flow/UI state
      // (auditId, busy flags, errors) must never survive a reload.
      partialize: (s) => ({
        token: s.token,
        tokenEnv: s.tokenEnv,
        tokenEmail: s.tokenEmail,
      }),
    },
  ),
);

// Wire the iv3api client's interceptor to this store: authenticated requests
// read the live token and, on 401, trigger a single refresh-and-retry.
configureIv3Auth({
  getToken: () => useOperatorAuthStore.getState().token,
  refresh: () => useOperatorAuthStore.getState().refresh(),
});
